import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { demeterMarketIndicatorsApi } from '../../../Apis/Apis';
import {
    DeleteMarketIndicatorFactorRequest,
    DemeterFilterTimeSpan,
    GetMarketIndicatorResponse,
    MarketIndicatorFactorSummaryModel,
    MarketIndicatorOutlook,
    MarketIndicatorTemplateType,
} from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import ImageChart from '../../Components/Charts/ImageChart/ImageChart';
import MarketIndicatorChart from '../../Components/Charts/MarketIndicator/MarketIndicatorChart';
import MarketIndicatorAverageSeasonalChart from '../../Components/Charts/MarketIndicators/MarketIndicatorAverageSeasonal/MarketIndicatorAverageSeasonalChart';
import MarketIndicatorDecileChart from '../../Components/Charts/MarketIndicators/MarketIndicatorDecile/MarketIndicatorDecileChart';
import MarketIndicatorForecastChart from '../../Components/Charts/MarketIndicators/MarketIndicatorForecast/MarketIndicatorForecastChart';
import MarketIndicatorHistoricalChart from '../../Components/Charts/MarketIndicators/MarketIndicatorHistorical/MarketIndicatorHistoricalChart';
import MarketIndicatorSeasonalChart from '../../Components/Charts/MarketIndicators/MarketIndicatorSeasonal/MarketIndicatorSeasonalChart';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import useLanguage from '../../Services/Language/useLanguageHook';
import trackingService from '../../Services/Tracking/TrackingService';
import {
    defaultSeasonalEndMonth,
    defaultSeasonalStartMonth,
} from '../Administration/MarketIndicatorsManagement/MarketIndicatorFactor/MarketIndicatorFactorPageDefault';
import {
    AverageSeasonalParameters,
    DecileParameters,
    HistoricalParameters,
    indicatorColorByOutlookDefinitions,
    SeasonalParameters,
} from '../Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import styles from './MarketIndicatorFactorsPage.module.scss';

interface IMarketIndicatorFactorSummaryProps {
    factor: MarketIndicatorFactorSummaryModel;
    getMarketIndicatorResponse?: GetMarketIndicatorResponse;
    groupDisplayName?: string;
    handleIndicatorDelete: (request: DeleteMarketIndicatorFactorRequest) => void;
    isInAdministrationMode?: boolean;
    isInEditMode?: boolean;
    isReadOnlyMode?: boolean;
}

// TODO - Permission service instead of "currentUserTypeIsCompanyAdministrator".
const MarketIndicatorFactorSummary: React.FC<IMarketIndicatorFactorSummaryProps> = (props: IMarketIndicatorFactorSummaryProps) => {
    // Application hooks and application constants.
    const [translations] = useLanguage();
    const { marketIndicatorGuid } = useParams();
    const [searchParameters] = useSearchParameters();
    const [showPopout, setShowPopout] = useState<boolean>(false);

    const factorOrActiveSubFactorForChart = props.factor.activeSubFactor ?? props.factor;
    const templateTypeIsAverageCount = factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.AverageCount;
    const templateTypeIsAverageSeasonal = factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.AverageSeasonal;
    const templateTypeIsSeasonal = factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Seasonal;
    const templateTypeIsTechnical = factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Technical;

    const [, refreshGetMarketIndicatorFactorDataResponse, getMarketIndicatorFactorDataResponse] = useApiWithoutAutoExecute(
        () => {
            if (!searchParameters.marketIndicatorGuid) {
                return null;
            }

            return demeterMarketIndicatorsApi.getMarketIndicatorFactorData(
                searchParameters.marketIndicatorGuid!,
                factorOrActiveSubFactorForChart.marketIndicatorFactorGuid,
            );
        },
        { errorMessage: translations.marketIndicators.singleFactorErrorMessage },
    );

    const [loadingRunMarketIndicatorFactorForChart, refreshRunMarketIndicatorFactorForChart, runMarketIndicatorFactorForChartResponse] =
        useApiWithoutAutoExecute(() => {
            if (!searchParameters.marketIndicatorGuid || !props.factor.marketIndicatorFactorGuid) {
                return null;
            }

            return demeterMarketIndicatorsApi.runMarketIndicatorFactorForChart(searchParameters.marketIndicatorGuid, props.factor.marketIndicatorFactorGuid);
        });

    // Need to reduce the data set size to the number of years in the market indicator.
    const filteredGetMarketIndicatorFactorDataResponse = useMemo(() => {
        if (!props.getMarketIndicatorResponse?.marketIndicator?.numberOfYears || !getMarketIndicatorFactorDataResponse) {
            return undefined;
        }

        const factorNumberOfYears = props.factor.yearsOfData
            ? Number(props.factor.yearsOfData)
            : props.getMarketIndicatorResponse.marketIndicator.numberOfYears;

        const startDate = moment().subtract(factorNumberOfYears, 'year').toDate();

        if (templateTypeIsAverageCount || templateTypeIsAverageSeasonal || templateTypeIsSeasonal) {
            startDate.setFullYear(startDate.getFullYear() - 1);
        }

        return {
            ...getMarketIndicatorFactorDataResponse,
            rows: getMarketIndicatorFactorDataResponse?.rows?.filter((x) => new Date(x.asOfDate) >= startDate) ?? [],
            additionalData:
                getMarketIndicatorFactorDataResponse?.additionalData?.map((dataItem) => ({
                    ...dataItem,
                    rows: dataItem.rows?.filter((x) => new Date(x.asOfDate) >= startDate) ?? [],
                })) ?? [],
        };
    }, [getMarketIndicatorFactorDataResponse, props.getMarketIndicatorResponse]);

    const badgeOutlook = useMemo(() => {
        if (props.factor.activeSubFactor && props.factor.activeSubFactor.outlook !== MarketIndicatorOutlook.Unavailable) {
            return props.factor.activeSubFactor.outlook;
        }
        return props.factor.outlook;
    }, [props.factor.outlook, props.factor.activeSubFactor]);

    const factorSummary = useMemo(
        () => (
            <div
                style={{
                    backgroundColor: indicatorColorByOutlookDefinitions.find((x) => x.marketIndicatorOutlook === badgeOutlook)?.indicatorColor,
                    color: indicatorColorByOutlookDefinitions.find((x) => x.marketIndicatorOutlook === badgeOutlook)?.textColor,
                }}
                className={styles.indicator_outlook}
            >
                {translations.marketIndicatorOutlook[badgeOutlook]}
            </div>
        ),
        [translations, badgeOutlook],
    );

    const handleOpenMarketFactorChart = () => {
        if (!showPopout) {
            refreshGetMarketIndicatorFactorDataResponse();
        }

        if (!showPopout && (templateTypeIsAverageCount || templateTypeIsTechnical)) {
            refreshGetMarketIndicatorFactorDataResponse();
            refreshRunMarketIndicatorFactorForChart();
        }

        setShowPopout(true);

        trackingService.track({ eventCategory: 'MarketIndicators', eventAction: 'OpenMarketIndicatorFactorClicked', eventValue: props.factor.displayName });
    };

    useEffect(() => {
        if (
            searchParameters.marketIndicatorGuid &&
            searchParameters.marketIndicatorFactorGuid &&
            searchParameters.marketIndicatorFactorGuid === props.factor.marketIndicatorFactorGuid
        ) {
            refreshGetMarketIndicatorFactorDataResponse();
            refreshRunMarketIndicatorFactorForChart();
            setShowPopout(true);
        }
    }, [props.factor.marketIndicatorFactorGuid, searchParameters.marketIndicatorFactorGuid, searchParameters.marketIndicatorGuid]);

    const chartDisplayName = useMemo(
        () => factorOrActiveSubFactorForChart?.chartDisplayName || factorOrActiveSubFactorForChart?.displayName,
        [factorOrActiveSubFactorForChart],
    );

    const customChartDisplayName = useMemo(() => {
        const displayName = factorOrActiveSubFactorForChart?.chartDisplayName || factorOrActiveSubFactorForChart?.displayName;

        return (
            <div className={styles.indicator_chart_title}>
                {displayName}
                <div
                    style={{
                        backgroundColor: indicatorColorByOutlookDefinitions.find((x) => x.marketIndicatorOutlook === badgeOutlook)?.indicatorColor,
                        color: indicatorColorByOutlookDefinitions.find((x) => x.marketIndicatorOutlook === badgeOutlook)?.textColor,
                    }}
                    className={styles.indicator_chart_title_outlook}
                >
                    {translations.marketIndicatorOutlook[badgeOutlook]}
                </div>
            </div>
        );
    }, [factorOrActiveSubFactorForChart, props.factor]);

    return (
        <>
            <button
                onClick={handleOpenMarketFactorChart}
                type="button"
                className={props.isInEditMode ? styles.indicator_administration : styles.indicator_user}
                data-testid={`MarketIndicatorFactor${props.factor.order}`}
            >
                <div className={styles.indicator_title}>{props.factor.displayName}</div>
                {props.isInEditMode && (
                    <CloseIcon
                        onClick={() =>
                            props.handleIndicatorDelete({
                                marketIndicatorGuid: marketIndicatorGuid ?? '',
                                marketIndicatorFactorGuid: props.factor.marketIndicatorFactorGuid,
                            })
                        }
                    />
                )}
                {props.isInEditMode || (props.isInAdministrationMode && props.isReadOnlyMode) ? (
                    <Link
                        // eslint-disable-next-line max-len
                        to={`${NavigationRoutes.AdministrationMarketIndicators}/${marketIndicatorGuid}/factorGroups/${props.groupDisplayName}/factors/${props.factor.marketIndicatorFactorGuid}/edit`}
                    >
                        {factorSummary}
                    </Link>
                ) : (
                    <>
                        {factorSummary}
                        {(templateTypeIsAverageSeasonal || templateTypeIsAverageCount) && (
                            <MarketIndicatorAverageSeasonalChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                runMarketIndicatorResponse={runMarketIndicatorFactorForChartResponse}
                                templateTypeIsAverageCount={templateTypeIsAverageCount}
                                parameters={
                                    {
                                        yearsOfData: factorOrActiveSubFactorForChart.yearsOfData,
                                        startMonth: factorOrActiveSubFactorForChart.startMonth,
                                        endMonth: factorOrActiveSubFactorForChart.endMonth,
                                    } as AverageSeasonalParameters
                                }
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                showOnlyAsPopout
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Decile && (
                            <MarketIndicatorDecileChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                parameters={
                                    {
                                        yearsOfData: factorOrActiveSubFactorForChart.yearsOfData,
                                        oneYearWeightedPercent: factorOrActiveSubFactorForChart.oneYearWeightedPercent,
                                    } as DecileParameters
                                }
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                showOnlyAsPopout
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Forecast && (
                            <MarketIndicatorForecastChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                showOnlyAsPopout
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Historical && (
                            <MarketIndicatorHistoricalChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                parameters={{ yearsOfData: factorOrActiveSubFactorForChart.yearsOfData } as HistoricalParameters}
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                showOnlyAsPopout
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Seasonal && (
                            <MarketIndicatorSeasonalChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                parameters={
                                    {
                                        yearsOfData: factorOrActiveSubFactorForChart.yearsOfData,
                                        startMonth: factorOrActiveSubFactorForChart.startMonth ?? `${defaultSeasonalStartMonth}`,
                                        endMonth: factorOrActiveSubFactorForChart.endMonth ?? `${defaultSeasonalEndMonth}`,
                                        subFactorCalculationTypes: factorOrActiveSubFactorForChart.subFactorCalculationTypes,
                                    } as SeasonalParameters
                                }
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                showOnlyAsPopout
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Technical && (
                            <MarketIndicatorChart
                                title={chartDisplayName ?? props.getMarketIndicatorResponse?.marketIndicator?.displayName ?? ''}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                                runTestMarketIndicatorFactorResponse={runMarketIndicatorFactorForChartResponse}
                                isLoading={loadingRunMarketIndicatorFactorForChart}
                                secondaryRunTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse}
                                useColorPalletteInLegend
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                showOnlyAsPopout
                                chartNavigatorTimeRange={DemeterFilterTimeSpan.ThreeMonths}
                                track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.Speculative && (
                            <MarketIndicatorChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                useProjectionChart
                                ignorePlotbands
                                showOnlyAsPopout
                                track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                            />
                        )}
                        {factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.ForwardCurve && (
                            <MarketIndicatorChart
                                title={chartDisplayName}
                                customTitle={customChartDisplayName}
                                marketIndicatorOutlook={props.factor?.outlook}
                                runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                                runTestMarketIndicatorFactorResponse={filteredGetMarketIndicatorFactorDataResponse!}
                                showPopout={showPopout}
                                setShowPopout={setShowPopout}
                                ignorePlotbands
                                showOnlyAsPopout
                                track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                            />
                        )}
                    </>
                )}
            </button>
            {(factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.ManualEntry ||
                factorOrActiveSubFactorForChart.templateType === MarketIndicatorTemplateType.SlidingWindowSeasonal) &&
                !!factorOrActiveSubFactorForChart.imageUrl && (
                    <ImageChart
                        title={chartDisplayName}
                        customTitle={customChartDisplayName}
                        marketIndicatorOutlook={props.factor?.outlook}
                        imageSource={factorOrActiveSubFactorForChart.imageUrl}
                        showPopout={showPopout}
                        setShowPopout={setShowPopout}
                        showOnlyAsPopout
                        testId="MarketIndicatorFactorChart"
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorFactorImageChart' }}
                    />
                )}
        </>
    );
};

export default MarketIndicatorFactorSummary;
